import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart,
  MessageCircle,
  Repeat2,
  Flag,
  X,
  UserPlus,
  UserCheck,
  Loader2,
  Trash2,
  Send,
  Coins,
  Trophy,
  Copy,
  Check as CheckIcon,
  Sparkles as SparklesIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { TipCreatorModal } from "./TipCreatorModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PostDetailModal = ({ post: initialPost, onClose, onUpdate }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(initialPost);
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const [following, setFollowing] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };
  const isMine = post?.user_id === user?.id;

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!initialPost?.id) return undefined;
    let alive = true;
    setPost(initialPost);

    (async () => {
      try {
        const [d, c] = await Promise.all([
          axios.get(`${API_URL}/api/community/posts/${initialPost.id}`, { headers: authHeader }),
          axios.get(`${API_URL}/api/community/posts/${initialPost.id}/comments`, { headers: authHeader }),
        ]);
        if (!alive) return;
        setPost(d.data);
        setFollowing(!!d.data.is_following_creator);
        setComments(c.data.items || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoadingComments(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [initialPost?.id]);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  if (!post) return null;

  const handleLike = async () => {
    try {
      const r = await axios.post(
        `${API_URL}/api/community/posts/${post.id}/like`,
        {},
        { headers: authHeader }
      );
      const next = { ...post, liked_by_me: r.data.liked, likes_count: r.data.likes_count };
      setPost(next);
      onUpdate?.(next);
    } catch (e) {
      toast.error("Could not update like.");
    }
  };

  const handleFollow = async () => {
    try {
      const r = await axios.post(
        `${API_URL}/api/community/users/${post.user_id}/follow`,
        {},
        { headers: authHeader }
      );
      setFollowing(r.data.following);
      toast.success(r.data.following ? `Following ${post.user_name}` : `Unfollowed`);
    } catch (e) {
      toast.error("Follow failed.");
    }
  };

  const [replyingTo, setReplyingTo] = useState(null);  // comment object being replied to
  const [replyDraft, setReplyDraft] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});  // {commentId: true}

  const handleComment = async () => {
    if (!commentDraft.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/community/posts/${post.id}/comments`,
        { content: commentDraft.trim() },
        { headers: authHeader }
      );
      setComments((prev) => [{ ...r.data, replies_preview: [], replies_count: 0 }, ...prev]);
      setCommentDraft("");
      const next = { ...post, comments_count: (post.comments_count || 0) + 1 };
      setPost(next);
      onUpdate?.(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post comment.");
    } finally {
      setSending(false);
    }
  };

  const handleReply = async (parentComment) => {
    if (!replyDraft.trim()) return;
    setSendingReply(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/community/posts/${post.id}/comments`,
        { content: replyDraft.trim(), parent_id: parentComment.id },
        { headers: authHeader }
      );
      setComments((prev) => prev.map((c) =>
        c.id === parentComment.id
          ? {
              ...c,
              replies_count: (c.replies_count || 0) + 1,
              replies_preview: [...(c.replies_preview || []), r.data].slice(-3),
            }
          : c
      ));
      setReplyDraft("");
      setReplyingTo(null);
      const next = { ...post, comments_count: (post.comments_count || 0) + 1 };
      setPost(next);
      onUpdate?.(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post reply.");
    } finally {
      setSendingReply(false);
    }
  };

  const loadAllReplies = async (commentId) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/community/comments/${commentId}/replies?limit=50`,
        { headers: authHeader }
      );
      setComments((prev) => prev.map((c) =>
        c.id === commentId ? { ...c, replies_preview: r.data.items || [] } : c
      ));
      setExpandedReplies((prev) => ({ ...prev, [commentId]: true }));
    } catch (_e) { /* silent */ }
  };

  const handleDeleteComment = async (cid) => {
    try {
      await axios.delete(`${API_URL}/api/community/comments/${cid}`, { headers: authHeader });
      setComments((prev) => prev.filter((c) => c.id !== cid));
      const next = { ...post, comments_count: Math.max(0, (post.comments_count || 0) - 1) };
      setPost(next);
      onUpdate?.(next);
    } catch (e) {
      toast.error("Could not delete comment.");
    }
  };

  const handleRemix = () => {
    const params = new URLSearchParams({
      remix_post_id: post.id,
      prompt: (post.prompt || "").slice(0, 1500),
    });
    if (post.kind === "video") {
      navigate(`/dashboard/ai-video?${params.toString()}`);
    } else {
      navigate(`/dashboard/designer?${params.toString()}`);
    }
    toast.success("Prompt copied — generate your own remix!");
    onClose();
  };

  const handleReport = async () => {
    const reason = window.prompt("Why are you reporting this post?");
    if (!reason || !reason.trim()) return;
    try {
      await axios.post(
        `${API_URL}/api/community/posts/${post.id}/report`,
        { reason: reason.trim() },
        { headers: authHeader }
      );
      toast.success("Thanks — we'll review this post.");
    } catch (e) {
      toast.error("Could not submit report.");
    }
  };

  const handleUnshare = async () => {
    if (!window.confirm("Remove this post from the community feed?")) return;
    try {
      await axios.delete(`${API_URL}/api/community/posts/${post.id}`, { headers: authHeader });
      toast.success("Post removed from community.");
      onUpdate?.({ ...post, status: "removed" });
      onClose();
    } catch (e) {
      toast.error("Could not remove.");
    }
  };

  const handleCopyPrompt = async () => {
    if (!post.prompt) {
      toast.error("This post has no prompt to copy.");
      return;
    }
    try {
      await navigator.clipboard.writeText(post.prompt);
      setPromptCopied(true);
      toast.success("Prompt copied to clipboard");
      setTimeout(() => setPromptCopied(false), 1800);
    } catch (e) {
      toast.error("Could not copy.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="community-post-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-5xl w-full max-h-[92vh] overflow-hidden shadow-2xl flex flex-col lg:flex-row my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Media */}
        <div className="bg-ink-950 flex-1 flex items-center justify-center min-h-[300px] lg:max-w-[60%]">
          {post.kind === "video" ? (
            <video
              src={post.media_url}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-[80vh] object-contain"
              data-testid={`post-detail-video-${post.id}`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.nextElementSibling;
                if (fallback) fallback.style.display = "flex";
              }}
            />
          ) : (
            <img
              src={post.media_url}
              alt=""
              className="max-w-full max-h-[80vh] object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.nextElementSibling;
                if (fallback) fallback.style.display = "flex";
              }}
            />
          )}
          <div
            className="hidden flex-col items-center justify-center gap-2 text-center px-8 text-white/80"
            data-testid={`post-detail-unavailable-${post.id}`}
          >
            <span className="text-4xl">📼</span>
            <p className="text-sm font-bold uppercase tracking-[0.18em]">Video unavailable</p>
            <p className="text-xs text-white/60 max-w-xs">
              This clip was lost during a recent platform update. The creator will need to re-publish.
            </p>
          </div>
        </div>

        {/* Side panel */}
        <div className="w-full lg:w-[420px] flex flex-col bg-white">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-ink-200">
            <Link
              to={`/dashboard/community/u/${post.user_id}`}
              className="flex items-center gap-3 group"
              onClick={onClose}
            >
              <span className="w-10 h-10 rounded-full bg-ink-950 text-white flex items-center justify-center text-sm font-black">
                {post.user_initial}
              </span>
              <div>
                <p className="text-sm font-bold text-ink-950 group-hover:underline">{post.user_name}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
                  Creator
                </p>
              </div>
            </Link>
            <div className="flex items-center gap-2">
              {!isMine && (
                <Button
                  size="sm"
                  variant={following ? "outline" : "default"}
                  onClick={handleFollow}
                  data-testid="community-follow-btn"
                  className={following ? "" : "bg-ink-950 text-white"}
                >
                  {following ? (
                    <>
                      <UserCheck className="w-3 h-3" />
                      Following
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-3 h-3" />
                      Follow
                    </>
                  )}
                </Button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-ink-100 rounded-full"
                data-testid="community-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Caption + prompt */}
          <div className="p-4 border-b border-ink-200 space-y-3">
            {post.is_top_weekend && post.top_rank && (
              <div
                data-testid="community-detail-rank-badge"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black shadow-md ${
                  post.top_rank === 1
                    ? "bg-gradient-to-r from-yellow-300 to-amber-500 text-ink-950"
                    : post.top_rank === 2
                    ? "bg-gradient-to-r from-slate-200 to-slate-400 text-ink-950"
                    : post.top_rank === 3
                    ? "bg-gradient-to-r from-orange-300 to-amber-700 text-white"
                    : "bg-gradient-to-r from-amber-400 to-coral-500 text-white"
                }`}
              >
                <Trophy className="w-3 h-3" />
                Top {post.top_rank} this weekend
              </div>
            )}
            {post.is_new_creator && !post.is_top_weekend && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-black tracking-[0.18em]">
                <SparklesIcon className="w-3 h-3" />
                FRESH VOICE
              </div>
            )}
            {post.caption && (
              <p className="text-sm text-ink-950 leading-relaxed">{post.caption}</p>
            )}
            {post.prompt && (
              <div className="bg-ink-50 rounded-2xl p-3 border border-ink-200">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
                    Prompt
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyPrompt}
                    data-testid="community-copy-prompt"
                    className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border transition-all ${
                      promptCopied
                        ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                        : "bg-white border-ink-300 text-ink-700 hover:border-ink-950"
                    }`}
                  >
                    {promptCopied ? (
                      <>
                        <CheckIcon className="w-3 h-3" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" /> Copy prompt
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-ink-700 line-clamp-4">{post.prompt}</p>
              </div>
            )}

            {post.is_top_weekend && (
              <div
                data-testid="community-top20-details"
                className="grid grid-cols-3 gap-2 text-center"
              >
                <div className="bg-white border border-ink-200 rounded-xl py-2">
                  <p className="text-lg font-black text-ink-950">{post.likes_count || 0}</p>
                  <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-ink-500">Likes</p>
                </div>
                <div className="bg-white border border-ink-200 rounded-xl py-2">
                  <p className="text-lg font-black text-ink-950">{post.comments_count || 0}</p>
                  <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-ink-500">Comments</p>
                </div>
                <div className="bg-white border border-ink-200 rounded-xl py-2">
                  <p className="text-lg font-black text-ink-950">
                    {(post.likes_count || 0) + (post.comments_count || 0)}
                  </p>
                  <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-ink-500">Score</p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={handleLike}
                data-testid="community-like-btn"
                className={
                  post.liked_by_me
                    ? "bg-coral-500 text-white hover:bg-coral-600"
                    : "bg-ink-100 text-ink-950 hover:bg-ink-200"
                }
              >
                <Heart className={`w-3 h-3 ${post.liked_by_me ? "fill-current" : ""}`} />
                {post.likes_count || 0}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRemix}
                data-testid="community-remix-btn"
              >
                <Repeat2 className="w-3 h-3" />
                Remix this
              </Button>
              {post.is_top_weekend && !isMine && (
                <Button
                  size="sm"
                  onClick={() => setTipOpen(true)}
                  data-testid="community-tip-btn"
                  className="bg-gradient-to-r from-amber-400 to-coral-500 text-white hover:from-amber-500 hover:to-coral-600"
                >
                  <Coins className="w-3 h-3" />
                  Tip
                </Button>
              )}
              {!isMine && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleReport}
                  data-testid="community-report-btn"
                  className="text-ink-500"
                >
                  <Flag className="w-3 h-3" />
                  Report
                </Button>
              )}
              {isMine && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleUnshare}
                  data-testid="community-unshare-btn"
                  className="text-rose-600"
                >
                  <Trash2 className="w-3 h-3" />
                  Unshare
                </Button>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[180px]">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
              <MessageCircle className="w-3 h-3 inline mr-1" />
              {comments.length} {comments.length === 1 ? "comment" : "comments"}
            </p>
            {loadingComments && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-ink-500" />
              </div>
            )}
            {!loadingComments && comments.length === 0 && (
              <p className="text-xs text-ink-500 text-center py-6">
                No comments yet. Be the first!
              </p>
            )}
            {comments.map((c) => (
              <div
                key={c.id}
                className="flex items-start gap-3 group"
                data-testid={`community-comment-${c.id}`}
              >
                <span className="w-7 h-7 shrink-0 rounded-full bg-ink-950 text-white flex items-center justify-center text-[10px] font-black">
                  {c.user_initial}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-ink-950">{c.user_name}</p>
                  <p className="text-sm text-ink-700 break-words">{c.content}</p>

                  {/* Reply controls */}
                  <div className="flex items-center gap-3 mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setReplyingTo(replyingTo?.id === c.id ? null : c);
                        setReplyDraft("");
                      }}
                      data-testid={`comment-${c.id}-reply-btn`}
                      className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 hover:text-ink-950"
                    >
                      Reply
                    </button>
                    {(c.replies_count || 0) > (c.replies_preview?.length || 0) && !expandedReplies[c.id] && (
                      <button
                        type="button"
                        onClick={() => loadAllReplies(c.id)}
                        data-testid={`comment-${c.id}-load-replies`}
                        className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-500 hover:underline"
                      >
                        View {c.replies_count} replies
                      </button>
                    )}
                  </div>

                  {/* Inline reply composer */}
                  {replyingTo?.id === c.id && (
                    <div className="flex items-center gap-2 mt-2" data-testid={`comment-${c.id}-reply-composer`}>
                      <input
                        autoFocus
                        value={replyDraft}
                        onChange={(e) => setReplyDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleReply(c);
                          }
                          if (e.key === "Escape") setReplyingTo(null);
                        }}
                        placeholder={`Reply to ${c.user_name}…`}
                        maxLength={500}
                        data-testid={`comment-${c.id}-reply-input`}
                        className="flex-1 px-3 py-1.5 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                      />
                      <Button
                        size="icon"
                        disabled={sendingReply || !replyDraft.trim()}
                        onClick={() => handleReply(c)}
                        data-testid={`comment-${c.id}-reply-submit`}
                        className="bg-blue-500 text-white h-7 w-7"
                      >
                        {sendingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      </Button>
                    </div>
                  )}

                  {/* Reply preview list */}
                  {(c.replies_preview || []).length > 0 && (
                    <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-ink-100"
                         data-testid={`comment-${c.id}-replies`}>
                      {c.replies_preview.map((r) => (
                        <div key={r.id} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-ink-300 text-white text-[9px] font-black flex items-center justify-center shrink-0">
                            {r.user_initial || "?"}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-ink-950">{r.user_name}</p>
                            <p className="text-xs text-ink-700 break-words">{r.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(c.user_id === user?.id || user?.is_admin) && (
                  <button
                    onClick={() => handleDeleteComment(c.id)}
                    className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-rose-600 transition-opacity"
                    title="Delete comment"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-ink-200 bg-ink-50">
            <div className="flex items-center gap-2">
              <input
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleComment();
                  }
                }}
                placeholder="Add a comment…"
                maxLength={500}
                data-testid="community-comment-input"
                className="flex-1 px-3 py-2 bg-white border border-ink-200 rounded-xl text-sm focus:outline-none focus:border-ink-950"
              />
              <Button
                size="icon"
                disabled={sending || !commentDraft.trim()}
                onClick={handleComment}
                data-testid="community-comment-submit"
                className="bg-ink-950 text-white"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {tipOpen && (
        <TipCreatorModal
          post={post}
          onClose={() => setTipOpen(false)}
          onSent={() => setTipOpen(false)}
        />
      )}
    </div>
  );
};

export default PostDetailModal;
